<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import CalendarResource from '@/resources/calendar'
import { RaceEventResource, PartyResource, RaceEntryResource, RacePresetUserResource } from '@/resources/launcher'
import EventForm from '../components/EventForm.vue'
import PartyName from '../components/PartyName.vue'

export default {
    components: {
        EventForm,
        PartyName
    },
    computed: {
        ...mapGetters(['userResource', 'isStaff', 'isApexClub']),
        calendarId() {
            return this.$route.params.calendarId
        },
        calendarIsHost() {
            return this.calendarResource.attributes.party_is_host
        },
        kioskMode() {
            if (!this.calendarResource) {
                return false
            }

            return this.calendarResource.attributes.launcher_kiosk_enabled
        },
        userHasEarlyEntry() {
            if (this.seated?.next?.user != this.userResource.id) {
                return false
            }

            const earlyEntranceDate = moment(this.seated.next?.time).subtract(20, 'minutes')
            return earlyEntranceDate && !this.seated.current && this.isApexClub && (moment() > earlyEntranceDate)
        },
    },
    watch: {
        controlEnabled(isEnabled, wasEnabled) {
            if (isEnabled === false && wasEnabled) {
                this.$router.push({ name: 'calendarControlWaitingRoom', params: { calendarId: this.calendarId } })
            }
        }
    },
    data() {
        return {
            loading: true,
            calendarResource: null,
            partyResource: null,
            eventResource: null,
            entryResource: null,
            presetResources: [],
            presetResource: null,
            seated: null,
            eventHasChanges: false,
            socketConnected: false,
            serverRunning: false,
            controlEnabled: false,
            countdown: null,
            intervalIDSeated: null
        }
    },
    methods: {
        async load() {
            this.calendarResource = await CalendarResource.detail(this.calendarId, { resolveRelated: true })
            const calAttrs = this.calendarResource.attributes
            const seatedExit = await this.fetchSeated()

            if (!seatedExit) {
                return false
            }

            this.intervalIDSeated = setInterval(() => this.fetchSeated(), 1e4)
            
            const [partyResource, eventResource, entryResource, presetResources] = await Promise.all([
                calAttrs.readonly_values.party ? PartyResource.detail(calAttrs.readonly_values.party) : null,
                calAttrs.event ? RaceEventResource.detail(calAttrs.event) : null,
                calAttrs.entry ? RaceEntryResource.detail(calAttrs.entry) : null,
                RacePresetUserResource.filterAll({ user: this.userResource.id })
            ])

            this.partyResource = partyResource
            this.eventResource = eventResource
            this.entryResource = entryResource
            this.presetResources = presetResources

            if (!this.eventResource) {
                this.addMessage('danger', 'No event associated with this seat. Use launcher first.')
                return false
            }

            this.eventResource.socketWatchForChanges()
            this.eventResource.emitter.on('open', () => this.onSocketOpen())
            this.eventResource.emitter.on('close', () => this.socketConnected = false)
            this.eventResource.emitter.on('server', (isRunning) => this.serverRunning = isRunning)
            this.eventResource.emitter.on('update', () => this.onEventUpdate())
            this.eventResource.emitter.on('peers', (eventData) => this.onPeerMessage(eventData))
            this.loading = false

            setTimeout(() => {
                this.loginUserParse()
                this.$refs.eventComponent.fetchPresetsForUser(this.userResource.id)
            }, 500)
        },
        async fetchSeated() {
            this.seated = await this.calendarResource.seated()

            if (this.isStaff || this.userHasEarlyEntry) {
                return true
            }

            if (!this.seated.current?.user && this.kioskMode) {
                this.kickToWaitingWithStatusCode('locked')
                return false
            } else if (this.seated.current?.user !== this.userResource.id) {
                this.kickToWaitingWithStatusCode('inUse')
                return false
            }

            return true
        },
        async createParty() {
            const partyName = (this.userResource) ? `${String(this.userResource)}'s Party` : `${String(this.calendarResource)}'s Party`
            const party = await PartyResource.host(this.calendarId, partyName)
            this.calendarResource.attributes.party_is_host = true
            return party
        },
        async deleteParty() {
            this.onPartyDelete()
            this.$nextTick(() => {
                this.partyResource.delete()
            })
        },
        kickToWaitingWithStatusCode(statusCode) {
            this.$router.replace({ name: 'calendarControlWaitingRoom', params: { calendarId: this.calendarId }, query: { statusCode } })
        },
        onSocketOpen() {
            this.socketConnected = true
            this.eventResource.socketSendPeers({ type: 'signal', value: 'WAKE' })
        },
        onEventUpdate() {
            
        },
        onPeerMessage(eventData) {
            if (eventData.type == 'signal' && eventData.value == 'RELOAD') {
                window.location.reload()
            }
        },
        onPartyUpdate() {
            this.eventResource.socketSendPeers({ type: 'signal', value: 'RELOAD' })
        },
        onPartyDelete() {
            location.reload()
        },
        onSave() {
            this.$refs.eventComponent.save()
        },
        async loginUserParse() {
            if (this.entryResource && this.$route.query.loginUser) {
                const userId = (this.$route.query.loginUser) ? this.$route.query.loginUser : this.userResource.id
                this.entryResource.changes = { user: userId }
                await this.entryResource.save()
            }
        }
    },
    async mounted() {
        try {
            await this.load()
        } catch(e) {
            if (e.response.status == 403) {
                const statusCode = (this.kioskMode) ? 'locked' : 'inUse'
                this.kickToWaitingWithStatusCode(statusCode)
            } else {
                throw e
            }
        }
    },
    beforeDestroy() {
        if (this.intervalIDSeated) {
            clearInterval(this.intervalIDSeated)
        }
    
        if (this.eventResource) {
            this.eventResource.socketClose()
        }
    }
}
</script>

<template>
    <div>
        <messages />
        <b-container v-if="!loading" class="pb-5">
            <b-row>
                <b-col md="8" offset-md="2">
                    <b-card v-if="partyResource" class="mt-3">
                        <b-row>
                            <b-col>
                                <strong><party-name :party="partyResource" @update="onPartyUpdate()" :editable="calendarIsHost" /></strong>
                            </b-col>

                            <b-col class="text-right" v-if="calendarIsHost">
                                <b-link @click="deleteParty()">Dissoudre la fête</b-link>
                            </b-col>
                        </b-row>
                    </b-card>

                    <b-button v-else variant="outline-secondary" @click="createParty()" class="col-md-12 my-3">Créer une fête</b-button>
                </b-col>
            </b-row>

            <b-row v-if="eventResource && partyResource">
                <b-col md="8" offset-md="2">
                    <event-form
                        ref="eventComponent"
                        :calendar-id="(calendarId)"
                        :calendar-only="!calendarIsHost"
                        :event="eventResource"
                        :party="partyResource"
                        @change="eventHasChanges = true"
                        @deleteParty="onPartyDelete"
                        class="mb-5 mt-3"
                        no-save
                        no-controls>
                        <template #user="data">
                            <b-input-group>
                                <b-input :value="data.userDisplayName" disabled />
                                <b-input-group-append v-if="data.user == userResource.id" is-text>
                                    <b-link @click="$refs.eventComponent.presetsOpenUserEditModal(data.user, data.calendar)" title="Ouvrir les préréglages de course" v-b-tooltip.hover><b-icon icon="collection-play" scale="0.7" /></b-link>
                                </b-input-group-append>
                            </b-input-group>
                        </template>
                    </event-form>

                    <b-button variant="secondary" class="d-none d-sm-block mb-5" @click="onSave()" block>Enregistrer</b-button>
                </b-col>
            </b-row>

            <b-row v-else>
                <b-col md="8" offset-md="2">
                    <event-form ref="eventComponent" :event="eventResource" no-save />
                    <b-button variant="secondary" @click="onSave()" class="col-sm-12 d-none d-sm-block">Enregistrer</b-button>
                </b-col>
            </b-row>
        </b-container>

        <b-container class="mt-3 d-block d-sm-none pt-3" id="taco"  fluid>
            <b-row>
                <b-col class="text-center text-muted">
                    <b-button variant="secondary" @click="onSave()" class="col-sm-12">Enregistrer</b-button>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<style lang="scss" scoped>
.blank-sizer {
    min-height: 500px;
}

#taco {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 999;
    background-color: var(--panel);
    color: var(--ink);
    border-top: 1px solid var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.28);
    padding-bottom: 1rem;
}
:root[data-theme='dark'] #taco {
    background-color: var(--panel-2);
    border-top-color: var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
</style>
